import SortSelect, { SORT_TYPE } from "Explore/Components/SortSelect";
import styles from "./ConceptAcademicInformation.module.scss";
import { getConceptNotablePapersById } from "Explore/Services/Concept";
import useExploreInfo from "Hooks/useExploreInfo";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { LoadingListEntityProfile, PlainSearch } from "Explore/Components";
import PublicationItem from "Explore/Components/PublicationItem";
import { Empty } from "antd";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { ParamsFilterKey } from "Config/filterSearchParams";
import InfinityPagination, {
  TOTAL_PAGE_PER_FETCH,
} from "Explore/Components/Pagination/InfinityPagination";
import { Button } from "@mui/material";

const ConceptPublicationList = () => {
  const { filter, updateSearchParams } = useFilterAndPagination();
  const { entity_id: conceptId } = useExploreInfo();
  const [data, setData] = useState({ data: {}, isLoading: false });
  const [hasNextPage, setHasNextPage] = useState(true);
  const [currentMaxPage, setCurrentMaxPage] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
  });
  const [sortData, setSortData] = useState({
    orderBy: null,
    order: null,
  });

  const prevFilter = useRef({});

  useEffect(() => {
    const _prevFilter = prevFilter.current;
    if (
      !_.isEqual(_prevFilter, {
        conceptId,
        [ParamsFilterKey.QUERY]: filter[ParamsFilterKey.QUERY],
        [ParamsFilterKey.KEYWORD_QUERY]: filter[ParamsFilterKey.KEYWORD_QUERY],
        page: pagination.page,
        rowsPerPage: pagination.rowsPerPage,
        orderBy: sortData.orderBy,
        order: sortData.order,
      })
    ) {
      let newPagination = { ...pagination };
      let newData = { ...data.data };
      if (
        _prevFilter.conceptId !== conceptId ||
        _prevFilter[ParamsFilterKey.QUERY] !== filter[ParamsFilterKey.QUERY] ||
        _prevFilter[ParamsFilterKey.KEYWORD_QUERY] !==
          filter[ParamsFilterKey.KEYWORD_QUERY] ||
        _prevFilter.rowsPerPage !== pagination.rowsPerPage ||
        _prevFilter.order !== sortData.order ||
        _prevFilter.orderBy !== sortData.orderBy
      ) {
        newData = {};
        newPagination.page = 1;
      }
      const pageToCompute = newPagination.page - 1;
      let pageToFetch = pageToCompute;
      for (
        let i = pageToCompute;
        i < pageToCompute + TOTAL_PAGE_PER_FETCH + 1;
        i++
      ) {
        pageToFetch = i;
        if (!newData[i]) {
          break;
        }
      }
      const numberOfPageToFetch =
        pageToCompute + TOTAL_PAGE_PER_FETCH - pageToFetch;

      if (!!numberOfPageToFetch) {
        setData({ data: newData, isLoading: pageToFetch === pageToCompute });
        getConceptNotablePapersById({
          id: conceptId,
          titleQuery: filter[ParamsFilterKey.QUERY],
          keyword: filter[ParamsFilterKey.KEYWORD_QUERY],
          page: pageToCompute,
          rowsPerPage: newPagination.rowsPerPage,
          numPages: numberOfPageToFetch,
          orderBy: sortData.orderBy,
          order: sortData.order,
        })
          .then((res) => {
            let i = 0;
            for (; i < numberOfPageToFetch; i++) {
              const pageData = res.results.slice(
                i * pagination.rowsPerPage,
                (i + 1) * pagination.rowsPerPage
              );
              if (pageData.length > 0) {
                newData[pageToFetch + i] = pageData;
              } else {
                break;
              }
            }
            setData({
              data: newData,
              isLoading: false,
            });
            setCurrentMaxPage((prev) => Math.max(prev, pageToFetch + i));
            setHasNextPage(res.has_next_page);
            setPagination(newPagination);
          })
          .catch((err) => {
            setData({ data: [], isLoading: false });
            setPagination({ ...newPagination, totalRows: 0 });
            setHasNextPage(false);
            setCurrentMaxPage(0);
          });
      }

      prevFilter.current = {
        conceptId,
        page: newPagination.page,
        rowsPerPage: newPagination.rowsPerPage,
        orderBy: sortData.orderBy,
        order: sortData.order,
        [ParamsFilterKey.QUERY]: filter[ParamsFilterKey.QUERY],
        [ParamsFilterKey.KEYWORD_QUERY]: filter[ParamsFilterKey.KEYWORD_QUERY],
      };
    } else {
      prevFilter.current = {
        conceptId,
        page: pagination.page,
        rowsPerPage: pagination.rowsPerPage,
        orderBy: sortData.orderBy,
        order: sortData.order,
        [ParamsFilterKey.QUERY]: filter[ParamsFilterKey.QUERY],
        [ParamsFilterKey.KEYWORD_QUERY]: filter[ParamsFilterKey.KEYWORD_QUERY],
      };
    }
  }, [
    conceptId,
    pagination.page,
    pagination.rowsPerPage,
    sortData.orderBy,
    sortData.order,
    filter[ParamsFilterKey.QUERY],
    filter[ParamsFilterKey.KEYWORD_QUERY],
  ]);
  return (
    <div className={styles["list__wrapper"]}>
      <div className={styles["list__header"]}>
        <SortSelect
          sortData={sortData}
          setSortData={setSortData}
          sortType={SORT_TYPE.PUBLICATION}
          hasOrderType
          loading={data.isLoading}
        />

        <div className={styles["list__header--search"]}>
          <PlainSearch
            className={styles["list__header--search--input"]}
            placeholder={"Enter to search"}
            filters={filter}
            setFilters={updateSearchParams}
            entity={ParamsFilterKey.QUERY}
            style={{ width: "100%", height: "32px" }}
          />
          <Button
            style={{ fontWeight: 500, textTransform: "none" }}
            color="error"
            disabled={!filter[ParamsFilterKey.QUERY]}
            onClick={() => {
              updateSearchParams({ [ParamsFilterKey.QUERY]: "" });
            }}
          >
            Clear
          </Button>
        </div>
      </div>

      <LoadingListEntityProfile
        isLoading={data.isLoading}
        component={
          data.data[pagination.page - 1]?.length > 0 ? (
            data?.data[pagination.page - 1].map((item, index) => {
              return <PublicationItem key={index} data={item} />;
            })
          ) : (
            <Empty description="No Publication Found." />
          )
        }
        componentNum={10}
        rows={2}
        avatar={true}
      />

      <InfinityPagination
        pagination={{ ...pagination }}
        setPagination={setPagination}
        sizeChanger
        hasNextPage={hasNextPage}
        currentMaxPage={currentMaxPage}
        isLoading={data.isLoading}
      />
    </div>
  );
};

export default ConceptPublicationList;
